import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {ObjectComponent} from 'features/core/shared/object/component/mvs-object/object.component';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {buildCpWidgetForm} from '../../../service/domain/cp-widget-column';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {WidgetSearchField} from 'features/core/shared/widget/service/provider/logic/search/widget-search-field';

/**
 * Agent area of the provision dashboard (route `/#/cp/dashboard/agents`, 04-frontend.md §7).
 *
 * STRUCTURE (PAT §K — Review Workbench):
 *   left  `#workbenchList`   — table QL widget `cp.dashboard.agents.table` on `cp.ProvisionAgent`,
 *                              join `agent` (am.Agent), reverse 1:1 join on cp.ProvisionAgentBalance
 *                              (Saldo real/simuliert), header quick search, toggle „Nur Konten mit Buchungen"
 *   right `#workbenchDetail` — `<mvs-object [objectType]="'cp.ProvisionAgent'">`; the registry
 *                              CP_ENTITY_PROVIDER resolves this to CpProvisionAgentObjectComponent
 *
 * WORKBENCH RULES observed here (skill fe_core_review-workbench, PAT §K.2):
 *   R1  `<mvs-review-workbench>` is the ONLY layout owner — no own splitter, no
 *       own height calculation; `autoFitViewport` + `viewportBottomGapRem` take care of that.
 *   R2  Visibility of the detail pane runs via `[panes]="selectedProvisionAgentId ? 2 : 1"`
 *       and NEVER via an `@if` around the workbench. PrimeNG's splitter builds its panel array
 *       exactly once in `onAfterContentInit`; a template swap would destroy the projected worklist
 *       — the table would remount, reload, and lose sorting/scroll position.
 *       That is why there is EXACTLY ONE instantiation point per slot.
 *   R3  Only the CONTENT of the detail pane is conditional, so an `<mvs-object>` without an id is
 *       never created.
 *
 * Reference usage site in the repo that this template follows:
 * `features/feature-core/ns/page/dashboard/ns-dashboard-overview/ns-dashboard-overview.component.html`
 * (lines 240–289) — the cleanest example of "entity in object mode on the right".
 *
 * AUTO-REFRESH (04-frontend.md §4): like every cp domain component this one subscribes to `status$`
 * of the job indicator that sits globally in the topbar and reloads on the running -> not-running
 * transition. Without it exactly the figures the balance job has just written (provision, reserve
 * and simulated balances per agent) would keep showing their pre-job values until somebody
 * navigates by hand. The indicator itself is neither built nor activated here — that is the shell
 * CpDashboardPage's job.
 */
@Component({
    selector: 'cp-provision-agents',
    templateUrl: './cp-provision-agents.component.html',
    styleUrls: ['./cp-provision-agents.component.scss'],
    standalone: false
})
export class CpProvisionAgentsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    /** Worklist on the left: all agents with the am.Agent name resolved. */
    agentsWidget: WidgetData;

    /** The currently selected agent; null = the workbench runs in 1-pane mode. */
    selectedProvisionAgentId: number = null;

    /**
     * The fields the header search runs over. `dataType` picks the operator in the framework
     * (`MvsWidgetListComponent.buildSearchFilter`): CP for text, EQ for numbers.
     *
     * NOT included: the Vermittlernummer (am.AgentNumber). It is 1:N to am.Agent (several partners,
     * several numbers), so a join would multiply the rows, and an `onlyFirst` join cannot be filtered
     * on from the root (fe_core_ql). Searching it needs a server-side search field.
     */
    static readonly SEARCH_FIELDS: WidgetSearchField[] = [
        {field: 'ag.name', formPath: 'ag.name', absoluteId: 'ag.name', label: 'Berater', dataType: 'String'},
        {field: 'e.agentId', formPath: 'e.agentId', absoluteId: 'e.agentId', label: 'Vermittler-Id', dataType: 'Long'},
        {field: 'e.id', formPath: 'e.id', absoluteId: 'e.id', label: 'Konto-Id', dataType: 'Long'}
    ];

    /** Narrow the worklist to accounts with bookings (a balance row exists). */
    onlyWithBookings: boolean = false;

    /** Is a cp job running right now? Feeds the hint above the worklist. */
    jobRunning: boolean;

    /**
     * The mounted instances. They exist ONLY for the in-place reload after a job:
     * `MvsWidgetComponent.refreshWidget()` and `ObjectComponent.refreshComponent()` reload WITHOUT
     * touching the bindings. That is what R2 demands — a freshly assigned `agentsWidget` or a
     * re-set `selectedProvisionAgentId` would rebuild the projected worklist resp. the detail pane
     * and lose sorting, scroll position and selection. Same build as
     * CpProvisionRunDetailComponent.reloadTabWidgets.
     */
    @ViewChild('agentsWidgetRef') agentsWidgetComponent: MvsWidgetComponent;
    @ViewChild('agentObjectRef') agentObjectComponent: ObjectComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;

    constructor(protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialization: builds the worklist widget. The detail pane is only created once a
     * selection is made.
     */
    initComponent(): void {
        this.refreshComponent();
        this.subscribeToJobCompletion();
        this.initialized = true;
    }

    /**
     * Subscribe to the job indicator and reload on the running -> not-running transition (04 §4).
     *
     * The agent figures are PRODUCED by the cp jobs: cp_provision_balance rewrites the balance rows,
     * cp_provision_calculation books the transactions behind them. A pane rendered while such a job
     * runs shows a half-finished state, and until this transition fires nothing tells the user the
     * numbers moved.
     *
     * Deliberately no confirm dialog: both panes are read-only surfaces here (edits happen in the
     * OBC's own dialogs), so a refresh has nothing to lose.
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadPanes();
            }
        });
    }

    /**
     * Reload both panes IN PLACE (R2) — the worklist and, if one is open, the selected Abrechnungskonto.
     *
     * The detail ref is `undefined` while nothing is selected (its content is conditional); the pane
     * then reads current data anyway as soon as a row is clicked.
     *
     * TRADE-OFF, deliberate: `ObjectComponent.refreshComponent()` rebuilds the dynamic object
     * component, so the OBC falls back to its default navItem. The alternative — leaving the detail
     * alone — would leave the four balance figures of an open Abrechnungskonto showing pre-job values
     * right after the balance job wrote new ones. In a money surface a stale figure is the worse of
     * the two, and this only ever happens at the moment a job the user was already watching ends.
     */
    protected reloadPanes(): void {
        this.agentsWidgetComponent?.refreshWidget();
        this.agentObjectComponent?.refreshComponent();
    }

    /**
     * Builds the worklist widget.
     *
     * QL shape following the documented repo example `bm-billing-analytics.component.ts`:
     * `start.type = 'entity'`, left join on the foreign key.
     *
     * ── WHY THE BERATER NAME USED TO BE MISSING (measured, not inferred)
     *
     * The table showed "Vermittler - ID" with a raw id and no name. The cause was NOT the query:
     * run against the E2E stack, the CURRENT query already returns `ag.name` = "E2E Test Agent" /
     * "E2E Zweitberater". A join without a `fields` list projects EVERY column of the joined entity,
     * `name` included.
     *
     * The cause was that this widget had NO `widgetForm`, so the generic QL table rendered all 32
     * projected columns — 21 of them am.Agent internals — and the name drowned among them while the
     * FK id column kept its metadata header. `buildCpWidgetForm` is the module's answer to exactly
     * this (see its TSDoc: every cp runtime table needs one); this widget was the one that lacked it.
     *
     * `resolve_fkey_texts` is left in place but does nothing for `agent`: the resolver skips a join
     * the query already declares (`QlDpEntityJdbcProviderImplResolver:89-91`). It still governs any
     * OTHER foreign key on the root entity, so removing it would be a silent change of scope.
     *
     * The explicit `fields: [{name: 'name'}]` is a payload cut, not the fix: it takes the projection
     * from 32 columns to 11 by dropping am.Agent's internals, which nothing here renders.
     */
    refreshComponent(): void {

        // Join 'bal' (balances): the reverse join is a genuine 1:1 (unique index
        // cp_provision_agent_balance_bkey), so it cannot multiply rows and takes NO `onlyFirst`
        // (fe_core_ql, reverse-join rule; same shape as the bm account's
        // `bm.CustomerBillingAccountBalance#customerBillingAccount`). Left join: an account without
        // bookings has no balance row and must still be listed.
        const qlQuery: QlQueryDto = {
            name: 'cp.dashboard.agents',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgent',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'agent', as: 'ag', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity', name: 'cp.ProvisionAgentBalance#provisionAgent', as: 'bal', joinType: 'left'
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            // Optional narrowing to the accounts that carry a balance row. The balance job writes that
            // row only for agents touched by a booking (ProvisionBalanceService
            // .recalcSimulatedForAffectedAgents), so "has a balance row" is "has bookings" — the cheap,
            // join-only way to cut 348 accounts down to the handful a run actually concerns.
            fixedFilters: [
                ...(this.onlyWithBookings ? [FilterCriteria.create('bal.id', FilterCriteria.cOperatorIsNotNull)] : [])
            ],
            // Sorted by the Berater name, because that is the column that IDENTIFIES a row. `e.id`
            // stays as the tie-breaker: two Berater can share a name (e.g. an active and a deleted
            // account of the same person), and without a deterministic second key their relative
            // order may differ between two fetches of the same page.
            sortings: [
                new Sorting('ag.name', true),
                new Sorting('e.id', true)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Worklist of all Abrechnungskonten (cp.ProvisionAgent) with the Berater name
         *         (join am.Agent), the Vermittler-Id and account id that tell duplicate names apart,
         *         the status, and the real and simulated provision balance (reverse 1:1 join
         *         cp.ProvisionAgentBalance). Quick search over name, Vermittler-Id and account id.
         */
        this.agentsWidget = WidgetFactory.createWidgetTableQl(
            'cp.dashboard.agents.table',
            'Abrechnungskonten',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S'),
            // The framework's header search (mvs-widget `enableSearch`): it ORs the search fields
            // into the request — CP on text, EQ on numbers when the input is numeric. Explicit
            // `searchFields`, because the derived default skips joined fields and FK ids, which
            // are exactly the three things a caseworker looks an account up by.
            WidgetDataParam.create('enableSearch', true),
            WidgetDataParam.create('searchFields', CpProvisionAgentsComponent.SEARCH_FIELDS),
            // Vermittler-Id and Konto-Id make two accounts with the same Berater name (one active,
            // one deleted) distinguishable; the Vermittler-Id is the number the run messages quote
            // (M-004 „Vermittler-Id: …"). Saldo real = ACTIVE bookings, Saldo simuliert = ACTIVE +
            // CALCULATED — the same two figures as the Kontoauszug header. `e.id` keeps feeding
            // handleAgentSelect regardless of the layout.
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'ag.name', label: 'Berater'},
                {field: 'e.agentId', label: 'Vermittler-Id'},
                {field: 'e.id', label: 'Konto-Id'},
                {field: 'e.entityStatus', label: 'Status'},
                {field: 'bal.provisionBalance', label: 'Saldo real'},
                {field: 'bal.provisionSimulatedBalance', label: 'Saldo simuliert'},
                {field: 'e.reservePercentage', label: 'Rücklage (%)'}
            ]))
        );
    }

    /** Toggle „Nur Konten mit Buchungen" — rebuilds the worklist with the narrowed query. */
    handleOnlyWithBookingsChange(): void {
        this.refreshComponent();
    }

    /**
     * Row selection in the worklist -> opens the detail pane.
     *
     * As in the ns original, an unreadable payload does NOT clear the selection but keeps it:
     * the table sends `onObjectSelect` only on a row click and has no deselection, so a missing
     * id is an error case — the detail pane must not unexpectedly go empty because of it.
     */
    handleAgentSelect(object: ObjectIdentifier | ObjectIdentifierData | any): void {

        const objectId = object?.objectId ?? object?.id ?? object?.data?.['e.id'];

        if (!objectId) {
            return;
        }

        this.selectedProvisionAgentId = Number(objectId);
    }

    /** Clears the selection — the workbench falls back into 1-pane mode. */
    handleClearSelection(): void {
        this.selectedProvisionAgentId = null;
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
    }

}
